// Physical Exam Display Mapping Utility
// Maps standardized parameter names to user-friendly display labels

import { physicalExamSectionsFull } from './physicalExamParametersFull';

/**
 * Creates a mapping from standardized parameter names to display labels
 */
const createDisplayMapping = () => {
  const standardizedToDisplayMap = {};
  const categoryMapping = {};
  
  physicalExamSectionsFull.forEach(section => {
    section.parameters.forEach(param => {
      standardizedToDisplayMap[param.key] = param.label;
      categoryMapping[param.key] = section.title;
    });
  });
  
  return {
    standardizedToDisplayMap,
    categoryMapping
  };
};

const { standardizedToDisplayMap, categoryMapping } = createDisplayMapping();

/**
 * Gets the display label for a standardized parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Human-readable display label
 */
export const getDisplayLabel = (standardizedKey) => {
  return standardizedToDisplayMap[standardizedKey] || standardizedKey;
};

/**
 * Gets the category for a standardized parameter name
 * @param {string} standardizedKey - Standardized parameter key
 * @returns {string} - Category name
 */
export const getParameterCategory = (standardizedKey) => {
  return categoryMapping[standardizedKey] || 'Unknown';
};

/**
 * Transforms flowsheet data for display by mapping parameter names to labels
 * @param {Array} flowsheetData - Array of flowsheet entries
 * @returns {Array} - Transformed data with display labels
 */
export const transformFlowsheetDataForDisplay = (flowsheetData) => {
  if (!Array.isArray(flowsheetData)) {
    return flowsheetData;
  }
  
  return flowsheetData.map(entry => {
    if (entry.type === 'PhysicalExamSummary' || entry.type === 'Physical Exam Detail') {
      return {
        ...entry,
        displayName: getDisplayLabel(entry.name),
        category: getParameterCategory(entry.name)
      };
    }
    return entry;
  });
};

/**
 * Checks if a parameter key is a standardized physical exam parameter
 * @param {string} parameterKey - Parameter key to check
 * @returns {boolean} - True if it's a standardized physical exam parameter
 */
export const isPhysicalExamParameter = (parameterKey) => {
  return typeof parameterKey === 'string' &&
         parameterKey.startsWith('pe_') &&
         standardizedToDisplayMap.hasOwnProperty(parameterKey);
};

/**
 * Gets all available physical exam parameters with their display information
 * @returns {Array} - Array of parameter objects with key, label, and category
 */
export const getAllPhysicalExamParameters = () => {
  const parameters = [];

  physicalExamSectionsFull.forEach(section => {
    section.parameters.forEach(param => {
      parameters.push({
        key: param.key,
        label: param.label,
        category: section.title,
        options: param.options,
        inputType: param.inputType
      });
    });
  });

  return parameters;
};

export default {
  getDisplayLabel,
  getParameterCategory,
  transformFlowsheetDataForDisplay,
  getAllPhysicalExamParameters
};
